import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import {
  Button,
  Input,
  Select,
  SelectItem,
  Tooltip,
} from "@heroui/react";
import React from "react";
import { useTranslation } from "react-i18next";

interface MarketplaceCardsFiltersProps {
  profileType: string;
  activeTab: "all" | "myCards";
  searchName: string;
  setSearchName: (value: string) => void;
  filterCardType: string;
  setFilterCardType: (value: string) => void;
  filterLocation: string;
  setFilterLocation: (value: string) => void;
  filterSport: string;
  setFilterSport: (value: string) => void;
  filterWeightClass: string;
  setFilterWeightClass: (value: string) => void;
  filterPurse: string;
  setFilterPurse: (value: string) => void;
  filterCurrency: string;
  setFilterCurrency: (value: string) => void;
  filterOpportunityType: string;
  setFilterOpportunityType: (value: string) => void;
  selectedRosterStatus: string;
  setSelectedRosterStatus: (value: string) => void;
  selectedTitle: string;
  setSelectedTitle: (value: string) => void;
  locations: string[];
  sports: string[];
  weightClasses: string[];
  purses: string[];
  opportunityTypes: string[];
  rosterStatus: string[];
  handleResetFilters: () => void;
}

const MarketplaceCardsFilters: React.FC<MarketplaceCardsFiltersProps> = ({
  profileType,
  activeTab,
  searchName,
  setSearchName,
  filterCardType,
  setFilterCardType,
  filterLocation,
  setFilterLocation,
  filterSport,
  setFilterSport,
  filterWeightClass,
  setFilterWeightClass,
  filterPurse,
  setFilterPurse,
  filterCurrency,
  setFilterCurrency,
  filterOpportunityType,
  setFilterOpportunityType,
  selectedRosterStatus,
  setSelectedRosterStatus,
  selectedTitle,
  setSelectedTitle,
  locations,
  sports,
  weightClasses,
  purses,
  opportunityTypes,
  rosterStatus,
  handleResetFilters,
}) => {
  const { t } = useTranslation("marketPlaceCards");

  const formatOption = (item: string) =>
    item
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase());
  const titleOptions = ["All Titles", "Yes", "No"];

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {(profileType === "MANAGER" ||
        profileType === "ORGANIZATION" ||
        profileType === "FIGHTER" ||
        profileType === "ADMIN") && (
        <>
          <Input
            placeholder={t("search_by_name")}
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            startContent={
              <MagnifyingGlassIcon className="w-4 h-4 text-gray-400" />
            }
            className="w-64 min-w-64"
            size="sm"
          />
          <Select
            placeholder={t("select_type")}
            selectedKeys={filterCardType ? [filterCardType] : []}
            onSelectionChange={(key) =>
              setFilterCardType(Array.from(key)[0] as string)
            }
            className="w-48 min-w-48"
            size="sm"
          >
            <SelectItem key="MANAGER">{t("manager")}</SelectItem>
            <SelectItem key="FIGHTER">{t("athlete")}</SelectItem>
            <SelectItem key="ORGANIZATION">{t("organization")}</SelectItem>
          </Select>
          <Select
            placeholder={t("select_location")}
            selectedKeys={filterLocation ? [filterLocation] : []}
            onSelectionChange={(key) =>
              setFilterLocation(Array.from(key)[0] as string)
            }
            className="w-48 min-w-48"
            size="sm"
          >
            {locations.map((loc) => (
              <SelectItem key={loc}>{formatOption(loc)}</SelectItem>
            ))}
          </Select>
        </>
      )}

      {(profileType === "FIGHTER" || profileType === "ADMIN") && (
        <>
          <Select
            placeholder={t("select_sport")}
            selectedKeys={filterSport ? [filterSport] : []}
            onSelectionChange={(key) =>
              setFilterSport(Array.from(key)[0] as string)
            }
            className="w-48 min-w-48"
            size="sm"
          >
            {sports.map((sport) => (
              <SelectItem key={sport}>{formatOption(sport)}</SelectItem>
            ))}
          </Select>
          <Select
            placeholder={t("select_weight_class")}
            selectedKeys={filterWeightClass ? [filterWeightClass] : []}
            onSelectionChange={(key) =>
              setFilterWeightClass(Array.from(key)[0] as string)
            }
            className="w-48 min-w-48"
            size="sm"
          >
            {weightClasses.map((wc) => (
              <SelectItem key={wc}>{formatOption(wc)}</SelectItem>
            ))}
          </Select>
          <Select
            placeholder={t("select_purse")}
            selectedKeys={filterPurse ? [filterPurse] : []}
            onSelectionChange={(key) =>
              setFilterPurse(Array.from(key)[0] as string)
            }
            className="w-48 min-w-48"
            size="sm"
          >
            {purses.map((purse) => (
              <SelectItem key={purse}>{formatOption(purse)}</SelectItem>
            ))}
          </Select>
          <Select
            placeholder={t("title_holder_placeholder")}
            selectedKeys={selectedTitle ? [selectedTitle] : []}
            onSelectionChange={(keys) =>
              setSelectedTitle((Array.from(keys)[0] as string) || "")
            }
            className="w-48 min-w-48"
            size="sm"
          >
            {titleOptions.map((option) => (
              <SelectItem key={option}>{option}</SelectItem>
            ))}
          </Select>
          <Select
            placeholder={t("select_roster_status")}
            selectedKeys={selectedRosterStatus ? [selectedRosterStatus] : []}
            onSelectionChange={(key) =>
              setSelectedRosterStatus(Array.from(key)[0] as string)
            }
            className="w-48 min-w-48"
            size="sm"
          >
            {rosterStatus.map((status) => (
              <SelectItem key={status}>{formatOption(status)}</SelectItem>
            ))}
          </Select>
        </>
      )}
      {(profileType === "MANAGER" ||
        profileType === "ORGANIZATION" ||
        profileType === "ADMIN") && (
        <>
          <Select
            placeholder={t("select_opportunity_type")}
            selectedKeys={filterOpportunityType ? [filterOpportunityType] : []}
            onSelectionChange={(key) =>
              setFilterOpportunityType(Array.from(key)[0] as string)
            }
            className="w-48 min-w-48"
            size="sm"
          >
            {opportunityTypes.map((opt) => (
              <SelectItem key={opt}>{formatOption(opt)}</SelectItem>
            ))}
          </Select>
        </>
      )}
      <Tooltip content={t("reset_filters")}>
        <Button
          isIconOnly
          variant="light"
          onClick={handleResetFilters}
          className="h-10 w-10 text-gray-600 hover:text-gray-800"
          size="sm"
        >
          <ArrowPathIcon className="w-5 h-5" />
        </Button>
      </Tooltip>
      <div className="flex-1" />
    </div>
  );
};

export default MarketplaceCardsFilters;
